import { z } from 'zod';

/**
 * The identifier a section, question or option is known by **forever**.
 *
 * Answers are addressed by key, not by database id, and a key is **immutable
 * once created** — that is the whole reason it exists. A staff member rewording
 * "What is your city?" into "Where do you live?" is editing translated content
 * (N5); the key underneath does not move, so every answer already given still
 * belongs to that question. This is the "answers survive a question label edit"
 * acceptance criterion, expressed as a data rule rather than a hope.
 *
 * Lower-case, snake_case, ASCII: keys travel into jsonb payloads, i18n key
 * suffixes, admin URLs and (for options) matching filters, so they must survive
 * every one of those without escaping.
 */
export const STABLE_KEY_PATTERN = /^[a-z][a-z0-9_]*$/;

export const StableKeySchema = z
  .string()
  .min(2)
  .max(64)
  .regex(
    STABLE_KEY_PATTERN,
    'A key is lower-case ASCII, starts with a letter, and uses underscores: e.g. "civil_registry_id".',
  );

export type StableKey = z.infer<typeof StableKeySchema>;

/** Narrows a plain string — what a route parameter passes through before a lookup. */
export function isStableKey(value: string): boolean {
  return StableKeySchema.safeParse(value).success;
}
